import { Component, Input, OnDestroy, OnInit, ViewChild, } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { DictionariesService } from 'app/services/dictionaries.service';
import { I18NService } from 'app/services/i18-n.service';
import { Subscription } from 'rxjs';
import { UpdateSelectedFacetsAction } from 'search/actions/facet.actions';
import { Facet, FacetResult, } from 'search/facets/models/resource-facet';
import { FacetsService } from 'search/facets/services/facets.service';
import { ResourceFacetBlock } from 'search/models/filter-panel';
import { FacetModalContext } from 'search/models/modal-window-data';
import { DEFAULT_LAZY_LOADED_FACET_SEARCH_OBJECT, FacetSearchObject, MappedSearchObject } from 'search/models/search-object';
import { getAdvancedMode, getSearchObject, State } from 'search/reducers/root.reducer';
import { FacetLabelService } from 'search/services/facet-label.service';
import { ConfigurationLoader } from 'shared/configuration-loader';
import { FeatureToggleService } from '../../../services/feature-toggle.service';

@Component({
  selector: 'app-facet-modal-content',
  templateUrl: './facet-modal-content.component.html',
  styleUrls: ['./facet-modal-content.component.scss'],
})
export class FacetModalContentComponent implements OnInit, OnDestroy {

  @Input() public context: FacetModalContext;
  @ViewChild('searchInput') public searchInput: any;
  public subscriptions = new Subscription();
  public inputControl: UntypedFormControl;
  public selectedFacets: Facet[] = [];
  public facetResults: FacetResult<Facet>;
  public facetSearch: FacetSearchObject;
  public prevQuery: string = null;
  public searchObject: MappedSearchObject;
  public initialFacetResults: FacetResult<Facet>;
  public isRapidoMode = false;

  private isAdvancedMode: boolean = false;

  constructor(private store: Store<State>,
              public facetsService: FacetsService,
              private dictionariesService: DictionariesService,
              private i18NService: I18NService,
              public activeModal: NgbActiveModal,
              public facetLabelService: FacetLabelService,
              private readonly configLoader: ConfigurationLoader,
              private readonly featureToggleService: FeatureToggleService
  ) {
  }

  public ngOnInit() {
    this.inputControl = new UntypedFormControl();
    this.selectedFacets = this.context.selectedFacets;

    this.subscriptions.add(
      this.store.select(getSearchObject).subscribe((data: MappedSearchObject) => {
        if (data) {
          this.facetSearch = {
            ...data,
            facetQuery: {},
            facetMapField: this.context.facetSchema.mapField,
            facetKey: this.context.facetSchema.key,
          };
          this.isRapidoMode = data.rapido;
        }
      })
    );

    this.subscriptions.add(
      this.store.select(getAdvancedMode).subscribe((advanced) => {
        this.isAdvancedMode = advanced;
      })
    );

    this.facetResults = this.markSelected(JSON.parse(JSON.stringify(this.context.facetResult)));
    this.facetResults = this.setLabel(this.facetResults);
    this.facetResults = this.applySortOptions(this.facetResults);
    this.initialFacetResults = JSON.parse(JSON.stringify(this.facetResults));
  }

  public selected(facet: Facet): boolean {
    return this.selectedFacets.some((el) => el.id === facet.id);
  }

  public onSearch() {
    const query = this.inputControl.value;

    if (this.prevQuery !== query) {
      this.prevQuery = query;
      if (query) {
        this.handleQuerySearch(query);
      } else {
        this.resetFacetSearch();
      }
      this.executeFacetSearch(query);
    }
  }

  private handleQuerySearch(query: string) {
    if (this.context.facetSchema.key === ResourceFacetBlock.FORMATS) {
      this.searchFormats(query);
    } else if (this.context.facetSchema.key === ResourceFacetBlock.LANGUAGE) {
      this.searchLanguages(query);
    } else {
      this.facetSearch.facetQuery.subString = query;
    }
  }

  private searchFormats(query: string) {
    const dictionaryItems = this.dictionariesService.findFormatByDesc(query);

    if (!dictionaryItems.length) {
      this.facetResults.data = [];
      this.facetSearch.facetQuery.codes = [];
    } else {
      this.facetSearch.facetQuery.codes = dictionaryItems.map(item => item.code);
    }
  }

  private searchLanguages(query: string) {
    const langCodes = this.i18NService.getLangCodesByDesc(query);

    if (!langCodes.length) {
      this.facetResults.data = [];
      return;
    }

    this.facetSearch.facetQuery.codes = langCodes;
  }

  private resetFacetSearch() {
    this.facetSearch.facetQuery = {};
    this.facetResults.data = JSON.parse(JSON.stringify(this.context.facetResult.data));
    this.facetResults = this.markSelected(this.facetResults);
    this.facetResults = this.setLabel(this.facetResults);
    this.facetResults = this.applySortOptions(this.facetResults);
  }

  private executeFacetSearch(query: string) {
    let args = [];
    const toggleService = this.featureToggleService.getToggles();

    if (this.context.facetSchema.mapField === 'location') {
      args.push(DEFAULT_LAZY_LOADED_FACET_SEARCH_OBJECT['location']);
    }

    if (this.context.facetSchema.mapField === 'materialType' && toggleService['VE-4775_2024-07-31_format_facet_search']) {
      args.push(DEFAULT_LAZY_LOADED_FACET_SEARCH_OBJECT['materialType']);
      if (query) {
        this.searchMaterialTypes(query);
        return;
      }
    }

    this.subscriptions.add(this.facetsService.getSingleFacet(this.facetSearch, ...args).subscribe((result: FacetResult<Facet>) => {
      this.facetResults = this.markSelected(result);
      this.facetResults = this.setLabel(this.facetResults);
      this.facetResults = this.applySortOptions(this.facetResults);
    }));
  }

  private searchMaterialTypes(query: string) {
    const data = this.initialFacetResults.data.map((item: any) => ({
      ...item,
      code: item.id,
      description: item.label,
      label: item.label
    }));

    const dictionaryItemsDefaults = data.filter(item => item.description.toLowerCase().includes(query.toLowerCase()));

    if (dictionaryItemsDefaults.length) {
      const formatCodes = dictionaryItemsDefaults.map(item => item.code);
      this.facetResults.data = this.context.facetResult.data.filter(item => formatCodes.includes(item.id));
      this.facetResults = this.markSelected(this.facetResults);
      this.facetResults = this.setLabel(this.facetResults);
    } else {
      this.facetResults.data = [];
    }
  }

  public onChange(checked: boolean, facet: Facet) {
    const index = this.selectedFacets.findIndex((value) => value.id === facet.id);
    if (checked) {
      if (index < 0) {
        this.selectedFacets.push(FacetModalContentComponent.wrapWithAdditionalFields(facet, this.context.facetSchema.key));
      }
      if (this.isAdvancedMode && !facet.isResetLocked) {
        const toggled = this.selectedFacets.find((toggled) => facet.id === toggled.id);
        facet.isResetLocked = true;
        toggled.isResetLocked = true;
      }
    } else {
      this.selectedFacets.splice(index, 1);
    }
    facet.selected = checked;
  }

  public onConfirm() {
    this.activeModal.close();
    this.store.dispatch(new UpdateSelectedFacetsAction({
      selected: this.selectedFacets,
      facetKey: this.context.facetSchema.key,
    }));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onToggleBubbleLock(target: Facet) {
    const toggled = this.selectedFacets.find((facet) => facet.id === target.id);
    toggled.isResetLocked = !toggled.isResetLocked;
    target.isResetLocked = !target.isResetLocked;
  }

  public resetFilters(): void {
    this.facetResults.data = this.facetResults.data.map((facet) => {
      return {
        ...facet,
        selected: false,
      };
    });
    this.selectedFacets = [];
  }

  public trackById(index: number, facet: Facet): string {
    return facet.id;
  }

  private static wrapWithAdditionalFields(facet: Facet, facetKey: string) {
    return {
      ...facet,
      facetKey,
      isResetLocked: false,
    };
  }

  private markSelected(facetResult: FacetResult<Facet>): FacetResult<Facet> {
    const facets = facetResult.data.map((facet) => {
      const index = this.selectedFacets.findIndex((selectedFacet) => facet.id === selectedFacet.id);
      if (index > -1) {
        return {
          ...facet,
          selected: true,
        };
      }
      return {...facet};
    });
    return {
      ...facetResult,
      data: facets.map((facet) => ({...facet, facetKey: this.context.facetSchema.key})),
    };
  }

  private setLabel(facetResult: FacetResult<Facet>): FacetResult<Facet> {
    return {
      ...facetResult,
      data: facetResult.data.map((facet) => {
        return {...facet, label: this.facetLabelService.getFacetLabel(facet)};
      })
    };
  }

  private applySortOptions(facetResult: FacetResult<Facet>): FacetResult<Facet> {
    if (this.context.facetSchema.mapField === 'location'
      && this.configLoader.displayConfiguration.sortLocationFacetAlphabetically) {
      return {
        ...facetResult,
        data: facetResult.data.sort((a, b) => a.label < b.label ? -1 : 1)
      };
    }
    return facetResult;
  }
}
